import type { ScopeSourceDefinition, SingleEntityStore } from '@shared/lib/scope'
import type { ScopeSource } from '@shared/lib/scope'
import type { ScopeStatus } from '@shared/lib/scope'

import { useAiSummarizeStore } from '@entities/ai-summary'
import { useAssessmentResultStore } from '@entities/assessment'
import { useAssessmentResultsStore } from '@entities/assessment'
import { useBookingStore } from '@entities/booking'
import { useDisciplineStore } from '@entities/customer'
import { useMasteryMapDashboardStore } from '@pages/discipline/mastery-map'
import { useProgressReportStore } from '@entities/progress-report'
import { useScheduleStore } from '@entities/schedule'
import { useBookingsApi } from '@entities/booking'
import { useCurrentBookingApi } from '@entities/booking'
import { useFinishedBookingsApi } from '@entities/booking'
import { useActiveDisciplineInfoApi } from '@entities/customer'
import { useProgressReportsListApi } from '@entities/progress-report'
import { useAssessmentResultsListApi } from '@entities/assessment'
import { useMasteryMapDashboardApi } from '@pages/discipline/mastery-map'
import { useAssessmentResultsDocumentApi } from '@entities/assessment'
import { useTutorAvailability } from '@entities/schedule'
import { useAiSummaryLoad } from '@entities/ai-summary'
import { useAssessmentResultLoad } from '@entities/assessment'

const activeEntityStatus = (store: SingleEntityStore, id: number | null): ScopeStatus =>
  store.activeId === id ? store.getStatus : 'idle'

export const DEFAULT_SCOPE_REQUIRES: ScopeSource[] = ['disciplineInfo']

export const SCOPE_SOURCES: Record<ScopeSource, ScopeSourceDefinition> = {
  disciplineInfo: {
    param: 'discipline',
    keepOnRevisit: true,
    status: (id) => useDisciplineStore().getInfoStatus(id),
    start: (id, mode) => {
      void useActiveDisciplineInfoApi({ disciplineId: id, mode })
    },
  },
  currentBooking: {
    param: 'discipline',
    status: (id) => useBookingStore().getCurrentStatus(id),
    start: (id, mode) => {
      void useCurrentBookingApi({ disciplineId: id, background: mode === 'refresh' })
    },
  },
  bookings: {
    param: 'discipline',
    status: (id) => useBookingStore().getListStatus(id),
    start: (id, mode) => {
      void useBookingsApi({ disciplineId: id, background: mode === 'refresh' })
    },
  },
  finishedBookings: {
    param: 'discipline',
    status: (id) => useBookingStore().getFinishedStatus(id),
    start: (id, mode) => {
      void useFinishedBookingsApi({ disciplineId: id, background: mode === 'refresh' })
    },
  },
  progressReports: {
    param: 'discipline',
    status: (id) => useProgressReportStore().getListStatus(id),
    start: (id, mode) => {
      void useProgressReportsListApi().getProgressReportList(id, mode)
    },
  },
  tutorAvailability: {
    param: 'discipline',
    status: (id) => useScheduleStore().getAvailabilityStatus(id),
    start: (id, mode) => {
      void useTutorAvailability(id, mode)
    },
  },
  aiSummary: {
    param: 'summarize',
    status: (id) => activeEntityStatus(useAiSummarizeStore(), id),
    start: (id) => {
      void useAiSummaryLoad().load(id)
    },
  },
  masteryDashboard: {
    param: 'discipline',
    status: (id) => useMasteryMapDashboardStore().getStatus(id),
    start: (id, mode) => {
      void useMasteryMapDashboardApi().fetchMasteryDashboard(id, mode)
    },
  },
  assessmentResult: {
    param: 'assessment',
    status: (id) => activeEntityStatus(useAssessmentResultStore(), id),
    start: (id) => {
      void useAssessmentResultLoad().load(id)
    },
  },
  assessmentResults: {
    param: 'discipline',
    keepOnRevisit: true,
    status: (id) => useAssessmentResultsStore().getListStatus(id),
    start: (id, mode) => {
      void useAssessmentResultsListApi().fetchAssessmentResults(id, mode)
    },
  },
  assessmentResultsDocument: {
    param: 'assessment',
    status: (id) => useAssessmentResultsStore().getResultStatus(id),
    start: (id, mode) => {
      void useAssessmentResultsDocumentApi().fetchAssessmentResult(id, mode)
    },
  },
}
